import React from 'react';
import { Edit, Trash2, Calendar, User, Tag } from 'lucide-react';
import { useRiskContext } from '../context/RiskContext.jsx';

export default function KanbanBoard() {
  const {
    filteredRisks,
    getKanbanColumns,
    kanbanGrouping,
    setKanbanGrouping,
    kanbanFieldMap,
    getRiskPriority,
    categoryColors,
    actionColors,
    openRiskDetail,
    removeRisk,
    draggedRisk,
    setDraggedRisk,
    dragOverColumn,
    setDragOverColumn,
    updateRisk,
    matrixConfig
  } = useRiskContext();

  const handleDragStart = (e, risk) => {
    setDraggedRisk(risk);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = (e) => {
    setDraggedRisk(null);
    setDragOverColumn(null);
  };

  const handleCardClick = (e, risk) => {
    // Only open if we're not dragging
    if (!draggedRisk) {
      openRiskDetail(risk);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, columnValue) => {
    e.preventDefault();
    setDragOverColumn(columnValue);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverColumn(null);
    }
  };

  const handleDrop = (e, newColumnValue) => {
    e.preventDefault();
    if (draggedRisk) {
      const fieldToUpdate = kanbanFieldMap[kanbanGrouping];
      updateRisk(draggedRisk.id, fieldToUpdate, newColumnValue);
    }
    setDraggedRisk(null);
    setDragOverColumn(null);
  };

  const getColumnRisks = (columnValue) => {
    const field = kanbanFieldMap[kanbanGrouping];
    return filteredRisks.filter(risk => risk[field] === columnValue);
  };

  return (
    <div className="space-y-4">
      {/* Kanban Grouping Selector */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-sm font-medium text-gray-700">Group by:</span>
        <select
          value={kanbanGrouping}
          onChange={(e) => setKanbanGrouping(e.target.value)}
          className="px-3 py-1 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="Status">Status</option>
          <option value="Category">Category</option>
          <option value="Action">Action</option>
          <option value="Owner">Owner</option>
        </select>
      </div>

      {/* Kanban Board */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {getKanbanColumns().map((columnValue) => {
          const columnRisks = getColumnRisks(columnValue);
          const isDragOver = dragOverColumn === columnValue;

          return (
            <div
              key={columnValue}
              className={`min-w-[280px] bg-gray-50 rounded-lg p-4 ${
                isDragOver ? 'bg-blue-50 border-2 border-blue-300' : ''
              }`}
              onDragOver={handleDragOver}
              onDragEnter={(e) => handleDragEnter(e, columnValue)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, columnValue)}
            >
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center justify-between">
                <span>{columnValue}</span>
                <span className="bg-gray-200 text-gray-600 text-xs px-2 py-1 rounded-full">
                  {columnRisks.length}
                </span>
              </h3>
              
              <div className="space-y-3">
                {columnRisks.map((risk) => {
                  const priority = getRiskPriority(risk.likelihood, risk.impact);
                  const isDragging = draggedRisk?.id === risk.id;
                  
                  // Enhanced tooltip with comprehensive risk information
                  const riskTooltip = [
                    `Risk #${risk.id}: ${risk.description}`,
                    `Priority: ${priority.level} (L:${matrixConfig.likelihoodLevels[risk.likelihood - 1]} × I:${matrixConfig.impactLevels[risk.impact - 1]})`,
                    risk.actionOwner ? `Owner: ${risk.actionOwner}` : 'No owner assigned',
                    risk.deadline ? `Deadline: ${new Date(risk.deadline).toLocaleDateString()}` : 'No deadline set',
                    risk.status === 'Overdue' ? '⚠️ OVERDUE' : `Status: ${risk.status}`,
                    '',
                    'Actions:',
                    '• Drag to move between columns',
                    '• Click to view/edit details',
                    '• Use buttons for quick actions'
                  ].join('\n');

                  return (
                    <div
                      key={risk.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, risk)}
                      onDragEnd={handleDragEnd}
                      className={`bg-white p-3 rounded-lg shadow-sm border cursor-move hover:shadow-md transition-all ${
                        isDragging ? 'opacity-50 transform rotate-2' : 'hover:scale-[1.02]'
                      }`}
                      onClick={(e) => {
                        // Only open detail if not clicking on buttons and not dragging
                        if (!e.target.closest('button') && !draggedRisk) {
                          openRiskDetail(risk);
                        }
                      }}
                      title={riskTooltip}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="text-gray-400 text-xs select-none pointer-events-none">
                            ⋮⋮
                          </div>
                          <span className="text-xs font-mono text-gray-500 select-none">#{risk.id}</span>
                        </div>
                        <div className="flex gap-1">
                          <button
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation();
                              openRiskDetail(risk);
                            }}
                            className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50 cursor-pointer"
                            title={`Edit Risk #${risk.id}\n• Update description, scores, and actions\n• Set deadlines and ownership\n• Track progress and status`}
                          >
                            <Edit size={12} />
                          </button>
                          <button
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(`Delete Risk #${risk.id}?\n\n"${risk.description}"\n\nThis action cannot be undone.`)) {
                                removeRisk(risk.id);
                              }
                            }}
                            className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 cursor-pointer"
                            title={`Delete Risk #${risk.id}\n⚠️ This will permanently remove:\n• Risk description and scores\n• All action plans and deadlines\n• Progress tracking data`}
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-800 mb-3 font-medium select-none">
                        {risk.description}
                      </p>
                      
                      <div className="space-y-2 select-none">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-600">
                            L: {matrixConfig.likelihoodLevels[risk.likelihood - 1]} × I: {matrixConfig.impactLevels[risk.impact - 1]}
                          </span>
                          <span
                            className="px-2 py-1 rounded text-white font-medium"
                            style={{ backgroundColor: priority.color }}
                          >
                            {priority.level}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          <Tag size={12} />
                          <span
                            className={`px-2 py-1 rounded ${categoryColors[risk.category] || 'bg-gray-500'} text-white`}
                          >
                            {risk.category}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2 text-xs">
                          <span
                            className={`px-2 py-1 rounded border ${actionColors[risk.action] || 'bg-gray-100 text-gray-800 border-gray-200'}`}
                          >
                            {risk.action}
                          </span>
                        </div>
                        
                        {risk.actionOwner && (
                          <div className="flex items-center gap-2 text-xs text-gray-600">
                            <User size={12} />
                            <span>{risk.actionOwner}</span>
                          </div>
                        )}
                        
                        {risk.deadline && (
                          <div className="flex items-center gap-2 text-xs text-gray-600">
                            <Calendar size={12} />
                            <span>{new Date(risk.deadline).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                
                {columnRisks.length === 0 && (
                  <div className="text-center text-gray-400 text-sm py-8 border-2 border-dashed border-gray-200 rounded-lg">
                    No risks in this column
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
} 